import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { api, GetPublishedWidgetData200Response } from "@/lib/api";
import TimeRange from "@/types/time-range";

export const keys = {
  // Namespace prefix for every widget data query. The dashboard id sits behind
  // the widget id in the key below, so this is the only handle on "all widget
  // data" (it covers the other dashboards in the cache too).
  publishedWidgetDataNamespace: () => ["publishedWidgetData"] as const,
  publishedWidgetData: (
    widgetId: string,
    dashboardId?: string,
    locationId?: string,
    timeRange?: TimeRange,
  ) => {
    return ["publishedWidgetData", widgetId, locationId, dashboardId, timeRange] as const;
  },
};

type PublishedWidgetDataQueryParams = {
  widgetId: string;
  dashboardId: string;
  locationId?: string;
  timeRange?: TimeRange;
  enabled?: boolean;
};

export const usePublishedWidgetDataQuery = ({
  widgetId,
  dashboardId,
  locationId,
  timeRange,
  enabled = true,
}: PublishedWidgetDataQueryParams) => {
  return useQuery({
    queryKey: keys.publishedWidgetData(widgetId, dashboardId, locationId, timeRange),
    queryFn: (): Promise<AxiosResponse<GetPublishedWidgetData200Response>> => {
      const { time_start, time_end } = timeRange?.toParamsObject() ?? {};
      return api.published.getPublishedWidgetData(
        dashboardId,
        widgetId,
        locationId,
        time_start,
        time_end,
      );
    },
    enabled: enabled && Boolean(widgetId && dashboardId),
  });
};
